import { useState, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Globe, Sparkles, Rss, FileText, Bot, Search, Zap, BarChart3,
  ListTodo, CheckSquare, Settings, ChevronLeft, ChevronRight, ChevronDown,
  Layers, Code, Activity, TrendingUp, Repeat, Tag, ShoppingCart, Share2,
  Smartphone, MessageSquare, MessageCircle, Plug, Puzzle, CreditCard, ShoppingBag,
  Shield, ImageIcon, Mail, Star, Send, Wallet, Link2, MousePointerClick, Twitter,
  Brain, Award, AlertTriangle, FileText as FileTextIcon, BookOpen, Beaker, Power,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { ADMIN_GROUPS } from "@/config/adminMenu";

const NON_ADMIN_MENU = {
  advertiser: [
    { section: "Głowne" },
    { path: "/", icon: LayoutDashboard, labelKey: "nav.dashboard" },
    { path: "/shop", icon: ShoppingBag, labelKey: "nav.shop" },
    { path: "/my-orders", icon: FileText, labelKey: "nav.myOrders" },
    { path: "/my-negotiations", icon: MessageSquare, labelKey: "nav.myNegotiations" },
    { path: "/zorki", icon: Star, labelKey: "nav.zorki" },
    { path: "/messages", icon: Send, labelKey: "nav.messages" },
    { section: "Moje" },
    { path: "/tasks", icon: CheckSquare, labelKey: "nav.tasks" },
    { path: "/settings", icon: Settings, labelKey: "nav.settings" },
  ],
  publisher: [
    { section: "Głowne" },
    { path: "/", icon: LayoutDashboard, labelKey: "nav.dashboard" },
    { path: "/shop", icon: ShoppingBag, labelKey: "nav.shop" },
    { path: "/zorki", icon: Star, labelKey: "nav.zorki" },
    { path: "/messages", icon: Send, labelKey: "nav.messages" },
    { section: "Panel Wydawcy" },
    { path: "/publisher", icon: Globe, labelKey: "nav.publisher" },
    { section: "Narzedzia" },
    { path: "/tasks", icon: CheckSquare, labelKey: "nav.tasks" },
    { path: "/settings", icon: Settings, labelKey: "nav.settings" },
  ],
};

const COLOR_CLASSES = {
  blue: "text-blue-400 hover:bg-blue-500/10",
  emerald: "text-emerald-400 hover:bg-emerald-500/10",
  amber: "text-amber-400 hover:bg-amber-500/10",
  purple: "text-purple-400 hover:bg-purple-500/10",
  pink: "text-pink-400 hover:bg-pink-500/10",
  orange: "text-orange-400 hover:bg-orange-500/10",
  cyan: "text-cyan-400 hover:bg-cyan-500/10",
  slate: "text-slate-400 hover:bg-slate-500/10",
};

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const role = user?.role || "advertiser";

  const activeGroupKey = useMemo(() => {
    if (role !== "admin") return null;
    // Wybierz grupe ktora ma item z najdluzszym prefiksem pasujacym do location.pathname
    let best = { key: null, len: -1 };
    for (const g of ADMIN_GROUPS) {
      for (const it of g.items) {
        if (it.path === "/") continue;
        if (location.pathname === it.path || location.pathname.startsWith(it.path + "/")) {
          if (it.path.length > best.len) best = { key: g.key, len: it.path.length };
        }
      }
    }
    if (location.pathname === "/") return "main";
    return best.key || "main";
  }, [location.pathname, role]);

  const [manuallyToggled, setManuallyToggled] = useState({});
  const isOpen = (key) => {
    if (key in manuallyToggled) return manuallyToggled[key];
    return key === activeGroupKey;
  };
  const toggle = (key) => setManuallyToggled((s) => ({ ...s, [key]: !isOpen(key) }));

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="sidebar"
        className={`${collapsed ? "w-16" : "w-64"} h-screen bg-slate-900 dark:bg-zinc-950 border-r border-slate-800 dark:border-zinc-800 flex flex-col transition-all duration-300 shrink-0`}
      >
        <div className="h-16 flex items-center px-4 border-b border-slate-800 dark:border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
              <Layers className="w-4 h-4 text-primary-foreground" strokeWidth={1.5} />
            </div>
            {!collapsed && (
              <span className="font-heading text-lg font-bold tracking-tight text-white">
                Epidemos
              </span>
            )}
          </div>
        </div>

        <ScrollArea className="flex-1 py-3">
          <nav className="flex flex-col gap-0.5 px-2">
            {role === "admin" ? (
              ADMIN_GROUPS.map((group) => {
                const GroupIcon = group.icon || Layers;
                const open = isOpen(group.key);
                const colorCls = COLOR_CLASSES[group.color] || COLOR_CLASSES.slate;

                if (collapsed) {
                  return (
                    <div key={group.key} className="mt-2">
                      {group.items.map((item) => {
                        const Icon = item.icon || Layers;
                        return (
                          <Tooltip key={item.path}>
                            <TooltipTrigger asChild>
                              <NavLink
                                to={item.path}
                                end={item.path === "/"}
                                className={({ isActive }) =>
                                  `flex items-center justify-center px-3 py-2 rounded-lg text-sm transition-all ${
                                    isActive
                                      ? "bg-primary text-white"
                                      : "text-slate-300 hover:text-white hover:bg-slate-800"
                                  }`
                                }
                              >
                                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                              </NavLink>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="font-medium">
                              {item.label}
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                      <Separator className="my-1 bg-slate-800" />
                    </div>
                  );
                }

                return (
                  <div key={group.key} className="mt-1">
                    <button
                      onClick={() => toggle(group.key)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${colorCls}`}
                      data-testid={`sidebar-group-${group.key}`}
                    >
                      <span className="flex items-center gap-2">
                        <GroupIcon className="w-4 h-4" strokeWidth={2} />
                        {group.label}
                      </span>
                      <ChevronDown
                        className={`w-3 h-3 transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
                      />
                    </button>
                    {open && (
                      <div className="mt-0.5 ml-2 flex flex-col gap-0.5 border-l border-slate-800 pl-2">
                        {group.items.map((item) => {
                          const Icon = item.icon || Layers;
                          return (
                            <NavLink
                              key={item.path}
                              to={item.path}
                              end={item.path === "/"}
                              title={item.desc}
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all ${
                                  isActive
                                    ? "bg-primary text-white shadow-sm"
                                    : "text-slate-300 hover:text-white hover:bg-slate-800"
                                }`
                              }
                            >
                              <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                              <span className="truncate">{item.label}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              (NON_ADMIN_MENU[role] || NON_ADMIN_MENU.advertiser).map((item, idx) => {
                if (item.section) {
                  return !collapsed ? (
                    <p key={idx} className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mt-3 mb-1 px-3">
                      {item.section}
                    </p>
                  ) : (
                    <Separator key={idx} className="my-2 bg-slate-700" />
                  );
                }
                const Icon = item.icon;
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={item.path}
                        end={item.path === "/"}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            isActive
                              ? "bg-primary text-white shadow-sm"
                              : "text-slate-300 hover:text-white hover:bg-slate-800"
                          } ${collapsed ? "justify-center" : ""}`
                        }
                      >
                        <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                        {!collapsed && <span>{item.labelKey ? t(item.labelKey) : item.label}</span>}
                      </NavLink>
                    </TooltipTrigger>
                    {collapsed && (
                      <TooltipContent side="right" className="font-medium">
                        {item.labelKey ? t(item.labelKey) : item.label}
                      </TooltipContent>
                    )}
                  </Tooltip>
                );
              })
            )}
          </nav>
        </ScrollArea>

        <Separator className="bg-slate-800" />

        <div className="p-2">
          <Button
            variant="ghost"
            size="sm"
            data-testid="sidebar-collapse-btn"
            onClick={() => setCollapsed(!collapsed)}
            className={`w-full ${collapsed ? "justify-center" : "justify-start"} text-slate-400 hover:text-white hover:bg-slate-800`}
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                <span className="text-xs">{t("nav.collapse")}</span>
              </>
            )}
          </Button>

          <LanguageSwitcher collapsed={collapsed} />

          {!collapsed && (
            <p className="text-[10px] text-slate-500 text-center mt-2">
              {t("common.version")} 1.0
            </p>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}

function LanguageSwitcher({ collapsed }) {
  const { language, setLanguage, t } = useLanguage();
  const languages = [
    { code: "pl", label: "PL", flag: "🇵🇱" },
    { code: "en", label: "EN", flag: "🇬🇧" },
  ];

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLanguage(language === "pl" ? "en" : "pl")}
            className="w-full justify-center text-slate-400 hover:text-white hover:bg-slate-800 mt-1"
          >
            <Globe className="w-4 h-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {language === "pl" ? "Switch to English" : "Przełącz na polski"}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className="flex items-center justify-center gap-1 mt-2">
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => setLanguage(lang.code)}
          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
            language === lang.code
              ? "bg-blue-600 text-white"
              : "text-slate-400 hover:text-white hover:bg-slate-700"
          }`}
          data-testid={`sidebar-lang-${lang.code}`}
        >
          {lang.flag} {lang.label}
        </button>
      ))}
    </div>
  );
}
